import { __commonJS, __toESM } from "./chunk-B4qE4VCv.mjs";
import { require_function } from "./function-CN-GVuiB.mjs";
import { require_Either } from "./Either-rFXaTLd5.mjs";
import { require_Option } from "./Option-D1XhUpjC.mjs";

//#region node_modules/.deno/fp-ts@2.16.10/node_modules/fp-ts/lib/Json.js
var require_Json = /* @__PURE__ */ __commonJS({ "node_modules/.deno/fp-ts@2.16.10/node_modules/fp-ts/lib/Json.js": ((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	/**
	* @since 2.10.0
	*/
	var Either_1 = require_Either();
	var function_1 = require_function();
	/**
	* Converts a JavaScript Object Notation (JSON) string into a `Json` type.
	*
	* @example
	* import * as J from 'fp-ts/Json'
	* import * as E from 'fp-ts/Either'
	* import { pipe } from 'fp-ts/function'
	*
	* assert.deepStrictEqual(pipe('{"a":1}', J.parse), E.right({ a: 1 }))
	* assert.deepStrictEqual(pipe('{"a":}', J.parse), E.left(new SyntaxError(`Unexpected token '}', "{"a":}" is not valid JSON`)))
	*
	* @since 2.10.0
	*/
	var parse = function(s) {
		return (0, Either_1.tryCatch)(function() {
			return JSON.parse(s);
		}, function_1.identity);
	};
	exports.parse = parse;
}) });

//#endregion
//#region src/local-storage.ts
var import_Json = /* @__PURE__ */ __toESM(require_Json());
var import_Option = /* @__PURE__ */ __toESM(require_Option());
var import_function = /* @__PURE__ */ __toESM(require_function());
/**
* Retrieves the value associated with the specified key from the local storage.
* Returns an `Option` that represents the value, or `None` if the key is not found.
* If run on the server it will always return `None`.
*
* @example
* ```ts twoslash
* import { localStorage } from '@simplisafe/ewok'
*
* const value = localStorage.safeGet('foo') // => O.some('bar')
* ```
*/
const safeGet = (key) => (0, import_function.pipe)(import_Option.tryCatch(() => localStorage.getItem(key)), import_Option.chain(import_Option.fromNullable));
/**
* Safely retrieves and parses a JSON value from the local storage.
*
* @example
* ```ts twoslash
* import { localStorage } from '@simplisafe/ewok'
*
* localStorage.set('object', { foo: 'bar' })
*
* localStorage.safeGetJson('object') // => O.some({ foo: 'bar' })
* ```
*/
const safeGetJson = (key) => (0, import_function.pipe)(import_Option.tryCatch(() => localStorage.getItem(key)), import_Option.chain(import_Option.fromNullable), import_Option.chain((0, import_function.flow)(import_Json.parse, import_Option.fromEither)));
/**
* Retrieves the value associated with the specified key from the local storage.
* Returns a string that represents the value, or null if the key is not found.
* If run on the server it will always return null.
*
* @example
* ```ts twoslash
* import { localStorage } from '@simplisafe/ewok'
*
* const value = localStorage.get('foo') // => 'bar'
* ```
*/
const get = (key) => (0, import_function.pipe)(safeGet(key), import_Option.toNullable);
/**
* Retrieves and parses a JSON value from the local storage.
*
* @example
* ```ts twoslash
* import { localStorage } from '@simplisafe/ewok'
*
* localStorage.set('object', { foo: 'bar' })
*
* localStorage.getJson('object') // => { foo: 'bar' }
* ```
*/
const getJson = (key) => (0, import_function.pipe)(safeGetJson(key), import_Option.toNullable);
/**
* Sets the value associated with the specified key in local storage.
* Stringifies the input value.
* If run on the server won't fail, but it won't set anything.
*
* @example
* ```ts twoslash
* import { localStorage } from '@simplisafe/ewok'
*
* const value = localStorage.set('foo', 'bar')
* ```
*/
const set = (key, value) => {
	(0, import_function.pipe)(import_Option.tryCatch(() => localStorage.setItem(key, typeof value === "string" ? value : JSON.stringify(value))));
};
/**
* Removes the value associated with the specified key in local storage.
* If run on the server won't fail, but it won't remove anything.
*
* @example
* ```ts twoslash
* import { localStorage } from '@simplisafe/ewok'
*
* const value = localStorage.remove('foo')
* ```
*/
const remove = (key) => {
	import_Option.tryCatch(() => localStorage.removeItem(key));
};
/**
* Removes all key/value pairs, if there are any.
* If run on the server won't fail, but it won't remove anything.
*
* @example
* ```ts twoslash
* import { localStorage } from '@simplisafe/ewok'
*
* const value = localStorage.clear()
* ```
*/
const clear = () => {
	import_Option.tryCatch(() => localStorage.clear());
};
const _localStorage = {
	get,
	set,
	safeGet,
	clear,
	remove,
	safeGetJson,
	getJson
};

//#endregion
export { _localStorage, clear, get, getJson, remove, safeGet, safeGetJson, set };